import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { safeFetch } from '../../utils/fetchJson';

const UP = '#10b981';
const DOWN = '#ef4444';
const IDLE = '#94a3b8';

function ageSeconds(ts) {
    if (!ts) return null;
    const d = new Date(String(ts).replace(' ', 'T'));
    if (Number.isNaN(d.getTime())) return null;
    return Math.max(0, Math.round((Date.now() - d.getTime()) / 1000));
}

function staleness(ts) {
    const s = ageSeconds(ts);
    if (s === null) return 'no data';
    if (s < 300) return 'flowing';
    if (s < 1800) return 'stale';
    return 'stalled';
}

const nodeBorder = (state) => state === 'up' ? 'rgba(52,211,153,0.5)' : state === 'down' ? 'rgba(248,113,113,0.5)' : 'var(--glass-border)';
const nodeBg = (state) => state === 'up' ? 'rgba(52,211,153,0.10)' : state === 'down' ? 'rgba(248,113,113,0.10)' : 'var(--glass)';

function FlowNode({ name, state, sub, pulse }) {
    const color = state === 'up' ? UP : state === 'down' ? DOWN : IDLE;
    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 84 }}>
            <div style={{ position: 'relative', borderRadius: 9, border: `1px solid ${nodeBorder(state)}`, background: nodeBg(state), padding: '8px 10px', textAlign: 'center' }}>
                <div className="flex items-center gap-1.5" style={{ justifyContent: 'center' }}>
                    <span style={{ position: 'relative', display: 'flex', width: 8, height: 8 }}>
                        {pulse && state === 'up' && <span style={{ animation: 'ping 1s infinite', position: 'absolute', display: 'inline-flex', width: '100%', height: '100%', borderRadius: '50%', opacity: 0.6, backgroundColor: color }} />}
                        <span style={{ position: 'relative', display: 'inline-flex', borderRadius: '50%', width: 8, height: 8, backgroundColor: color }} />
                    </span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-1)', whiteSpace: 'nowrap' }}>{name}</span>
                </div>
                {sub && <div className="muted" style={{ fontSize: 9, marginTop: 2, whiteSpace: 'nowrap' }}>{sub}</div>}
                {state === 'down' && <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--danger)', marginTop: 2, textTransform: 'uppercase' }}>Down</div>}
            </div>
        </div>
    );
}

function Arrow({ state = 'up' }) {
    const color = state === 'down' ? DOWN : UP;
    return (
        <div className="flex items-center" style={{ flexShrink: 0, width: 20, height: '100%', paddingTop: 16 }}>
            <svg width="20" height="10" viewBox="0 0 20 10">
                <line x1="0" y1="5" x2="16" y2="5" stroke={color} strokeWidth="1.5" />
                <path d="M14 2 L19 5 L14 8" fill="none" stroke={color} strokeWidth="1.5" />
            </svg>
        </div>
    );
}

function Lane({ title, children }) {
    return (
        <div>
            <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>{title}</div>
            <div className="flex flex-wrap" style={{ alignItems: 'flex-start', gap: 2 }}>{children}</div>
        </div>
    );
}

export default function PlatformFlow() {
    const { data: health } = useQuery({ queryKey: ['pf', 'health'], queryFn: () => safeFetch('/api/health'), staleTime: 20000, refetchInterval: 30000, retry: false });
    const { data: status } = useQuery({ queryKey: ['pf', 'status'], queryFn: () => safeFetch('/api/status'), staleTime: 20000, refetchInterval: 30000, retry: false });
    const { data: pipeline } = useQuery({ queryKey: ['pf', 'pipeline'], queryFn: () => safeFetch('/api/v1/clickstat/pipeline?hours=1'), staleTime: 45000, refetchInterval: 60000, retry: false });

    const containers = status?.containers || {};
    const ext = status?.external_services || {};
    const components = health?.components || {};
    const isUp = (name) => (containers[name] === 'running');
    const compUp = (name) => !!components[name];
    const contState = (name) => (name in containers ? (containers[name] === 'running' ? 'up' : 'down') : 'unknown');

    const WORKERS = ['synoti-worker-1', 'synoti-worker-2', 'synoti-worker-3', 'synoti-worker-4', 'synoti-worker-5', 'synoti-worker-6', 'synoti-worker-7', 'synoti-worker-8'];
    const workersUp = WORKERS.some(w => containers[w] === 'running');
    const workersKnown = WORKERS.some(w => w in containers);
    const workersState = workersUp ? 'up' : (workersKnown ? 'down' : 'unknown');

    const pipelineFlow = staleness(pipeline?.raw_last_ts);
    const canonFlow = staleness(pipeline?.canonical_last_ts);
    const pipelineState = pipelineFlow === 'flowing' ? 'up' : pipelineFlow === 'stalled' ? 'down' : 'up';
    const chState = ext.clickhouse?.status === 'ok' ? 'up' : 'unknown';
    const redisState = ext.redis?.connected === true ? 'up' : 'down';
    const aiState = ext.ai_engine?.status === 'ok' ? 'up' : 'unknown';
    const dbState = (health?.database === true || health?.database === 'ok') ? 'up' : 'down';

    return (
        <div style={{ background: 'var(--glass)', border: '1px solid var(--glass-border)', borderRadius: 9, padding: 12, boxShadow: 'var(--shadow)' }}>
            <div className="flex between center" style={{ marginBottom: 10 }}>
                <h2 style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-1)', textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>Platform Flow</h2>
                <span style={{ fontSize: 9, color: 'var(--text-3)' }}>SYNOTI pipeline — failed stages shown in red</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <Lane title="Data">
                    <FlowNode name="Filebeat" state={contState('filebeat')} sub="log shipper" />
                    <Arrow state={contState('filebeat')} />
                    <FlowNode name="Kafka" state={contState('kafka')} sub="synoti-logs-v2" />
                    <Arrow state={contState('kafka')} />
                    <FlowNode name="Log Pipeline" state={compUp('log_pipeline') ? pipelineState : 'down'} sub={pipeline ? `${(pipeline.raw_1h || 0).toLocaleString()} logs/h` : ''} pulse />
                    <Arrow state={pipelineState} />
                    <FlowNode name="ClickHouse" state={chState} sub={pipeline ? `${(pipeline.canonical_1h || 0).toLocaleString()} events/h` : ''} pulse={canonFlow === 'flowing'} />
                </Lane>
                <Lane title="Monitoring & Alerts">
                    <FlowNode name="Prometheus" state={contState('prometheus')} sub="metrics" />
                    <Arrow state={contState('prometheus')} />
                    <FlowNode name="Alert Manager" state={compUp('alert_manager') ? 'up' : 'down'} sub="rules" />
                    <Arrow state={compUp('alert_manager') ? 'up' : 'down'} />
                    <FlowNode name="Redis Queue" state={redisState} sub="alerts:queue" />
                    <Arrow state={redisState} />
                    <FlowNode name="Incidents (DB)" state={dbState} sub="postgresql" />
                </Lane>
                <Lane title="Engine">
                    <FlowNode name="Core API" state={contState('synoti-core')} sub=":8000" />
                    <Arrow state={contState('synoti-core')} />
                    <FlowNode name="Workers" state={workersState} sub="SSH agents" />
                    <Arrow />
                    <FlowNode name="ChatOps" state={contState('synoti-chatops')} sub="Telegram" />
                    <Arrow />
                    <FlowNode name="Self-Healing" state={compUp('self_healing_agent') ? 'up' : 'down'} sub="supervisor" pulse={compUp('self_healing_agent')} />
                </Lane>
                <Lane title="Security">
                    <FlowNode name="Wazuh" state={contState('wazuh')} sub="SIEM + EDR" />
                    <Arrow state={contState('wazuh')} />
                    <FlowNode name="Anomaly Detector" state={compUp('incident_detector') ? 'up' : 'down'} sub="events" />
                    <Arrow />
                    <FlowNode name="AI Engine" state={aiState} sub="ollama" />
                    <Arrow />
                    <FlowNode name="Security Analytics" state={pipeline ? 'up' : 'unknown'} sub="ClickHouse" />
                </Lane>
            </div>
        </div>
    );
}
